import { useRef, useState } from "react";

import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useReorderQuoteRequestLines } from "#modules/quoteRequestLines/view/useReorderQuoteRequestLines";
import { useStrategy } from "#modules/quoteRequestLines/view/useStrategy";
import {
  buildQuoteRequestLineTableRows,
  getQuoteRequestLineRowDragGroupKey,
  isLoadedQuoteRequestLineTableRow,
  QuoteRequestLineTableRow,
  sortByCreatedAt,
} from "#modules/quoteRequestLinesV2/domain/buildQuoteRequestLineTableRows";
import { optimisticallyApplyReorderById } from "#modules/quoteRequestLinesV2/domain/optimisticallyApplyReorderById";
import { searchQuoteRequestLinesByReference } from "#modules/quoteRequestLinesV2/domain/searchQuoteRequestLinesByReference";
import { useLiveQuoteRequestLineParts } from "#modules/quoteRequestLinesV2/view/useLiveQuoteRequestLineParts";

export const useQuoteRequestLineTableRows = (
  quoteRequestId: string | undefined,
  quoteRequestLines: (QuoteRequestLineDto | null | undefined)[],
  searchText: string,
) => {
  const isSearching = searchText.trim().length > 0;

  const { isClustering, modelType } = useStrategy();
  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequestId);
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequestId);
  const { reorderQuoteRequestLinesAsync } = useReorderQuoteRequestLines({ quoteRequestId });

  const liveQuoteRequestLines = useLiveQuoteRequestLineParts(quoteRequestLines);

  const [dragOverrideOrderedIds, setDragOverrideOrderedIds] = useState<string[] | null>(null);
  const reorderedQuoteRequestLines = optimisticallyApplyReorderById(
    liveQuoteRequestLines,
    dragOverrideOrderedIds,
  );

  const [expandedAssemblyIds, setExpandedAssemblyIds] = useState<Set<string>>(new Set());
  const [collapsedOverrideAssemblyIds, setCollapsedOverrideAssemblyIds] = useState<Set<string>>(
    new Set(),
  );

  const previousSearchTextRef = useRef(searchText);
  if (previousSearchTextRef.current !== searchText) {
    previousSearchTextRef.current = searchText;
    if (collapsedOverrideAssemblyIds.size > 0) {
      setCollapsedOverrideAssemblyIds(new Set());
    }
  }

  const lines = reorderedQuoteRequestLines.filter((l): l is QuoteRequestLineDto => Boolean(l));

  const searchResult = isSearching ? searchQuoteRequestLinesByReference(lines, searchText) : null;

  const effectiveExpandedAssemblyIds = searchResult
    ? new Set(
        [...expandedAssemblyIds, ...searchResult.forcedExpandedAssemblyIds].filter(
          (id) => !collapsedOverrideAssemblyIds.has(id),
        ),
      )
    : expandedAssemblyIds;

  const toggleAssemblyCollapse = (assemblyId: string) => {
    if (effectiveExpandedAssemblyIds.has(assemblyId)) {
      setExpandedAssemblyIds((prev) => {
        const next = new Set(prev);
        next.delete(assemblyId);
        return next;
      });
      if (isSearching) {
        setCollapsedOverrideAssemblyIds((prev) => new Set(prev).add(assemblyId));
      }
    } else {
      setExpandedAssemblyIds((prev) => new Set(prev).add(assemblyId));
      setCollapsedOverrideAssemblyIds((prev) => {
        if (!prev.has(assemblyId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(assemblyId);
        return next;
      });
    }
  };

  const rows = buildQuoteRequestLineTableRows(
    lines,
    effectiveExpandedAssemblyIds,
    { isClustering, modelType },
    optimizedMachineTimes,
    optimizedMaterialCosts,
    searchResult?.visibleLineIds,
  );

  const collapsedByDragIdRef = useRef<string | null>(null);

  const handleDragStart = (row: QuoteRequestLineTableRow) => {
    if (
      isLoadedQuoteRequestLineTableRow(row) &&
      row.isAssemblyParent &&
      effectiveExpandedAssemblyIds.has(row.line.id)
    ) {
      collapsedByDragIdRef.current = row.line.id;
      toggleAssemblyCollapse(row.line.id);
    }
  };

  const handleDragSettled = () => {
    if (collapsedByDragIdRef.current) {
      toggleAssemblyCollapse(collapsedByDragIdRef.current);
      collapsedByDragIdRef.current = null;
    }
  };

  const handleRowOrderChange = async (
    newRows: QuoteRequestLineTableRow[],
    movedRow: QuoteRequestLineTableRow,
  ) => {
    if (!quoteRequestId) {
      return;
    }

    const groupKey = getQuoteRequestLineRowDragGroupKey(movedRow);
    const orderedIds = newRows
      .filter((row) => getQuoteRequestLineRowDragGroupKey(row) === groupKey)
      .flatMap((row) =>
        isLoadedQuoteRequestLineTableRow(row) && row.assembliesInBatch.length > 1
          ? sortByCreatedAt(row.assembliesInBatch).map((assembly) => assembly.quoteRequestLine.id)
          : [row.line.id],
      );

    setDragOverrideOrderedIds(orderedIds);
    try {
      await reorderQuoteRequestLinesAsync({ quoteRequestId, orderedIds });
    } finally {
      setDragOverrideOrderedIds(null);
    }
  };

  return {
    rows,
    isSearching,
    expandedAssemblyIds: effectiveExpandedAssemblyIds,
    toggleAssemblyCollapse,
    handleDragStart,
    handleDragSettled,
    handleRowOrderChange,
  };
};
